import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type JurisdictionProfile = {
  id: string;
  name: string;
  cityName: string;
  countyName: string | null;
  stateName: string | null;
  isDefault: boolean;
  createdAt: string;
};

type AssumptionProfile = {
  id: string;
  name: string;
  isDefault: boolean;
  createdAt: string;
};

type AuthorizationRecord = {
  id: string;
  kind: 'vendor_setup' | 'profile_change' | 'consultant_override';
  signerName: string;
  signerTitle: string;
  documentUrl: string;
  signedAt: string;
  revokedAt: string | null;
  scope: Record<string, unknown> | null;
};

const YEAR_MS = 365 * 24 * 60 * 60 * 1000;

function authStatus(r: AuthorizationRecord): 'active' | 'expired' | 'revoked' {
  if (r.revokedAt) return 'revoked';
  const signedMs = Date.parse(r.signedAt);
  if (!Number.isFinite(signedMs)) return 'expired';
  return Date.now() - signedMs <= YEAR_MS ? 'active' : 'expired';
}

/**
 * Vendor super-admin governance panel — ADR-029 Phase 5.
 * Route: /admin/orgs/:orgId/governance
 *
 * Lists the target org's profiles and authorization records, and lets the
 * vendor record a signed `vendor_setup` (unblocking the org's first analysis)
 * or revoke an existing record. Profile create/edit forms are intentionally
 * scoped to the minimal fields needed for bootstrap; detailed tuning happens
 * inside the org's own settings UI once an owner is active.
 */
export function AdminOrgGovernanceRoute() {
  const { orgId } = useParams<{ orgId: string }>();
  const getToken = useSessionTokenGetter();
  const [governanceEnabled, setGovernanceEnabled] = useState<boolean | null>(null);
  const [togglingGov, setTogglingGov] = useState(false);
  const [jps, setJps] = useState<JurisdictionProfile[]>([]);
  const [aps, setAps] = useState<AssumptionProfile[]>([]);
  const [records, setRecords] = useState<AuthorizationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New-authorization form state
  const [formKind, setFormKind] = useState<AuthorizationRecord['kind']>('vendor_setup');
  const [formSigner, setFormSigner] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [formSignedAt, setFormSignedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [formProfileType, setFormProfileType] = useState<'jurisdiction' | 'assumption'>(
    'jurisdiction',
  );
  const [saving, setSaving] = useState(false);

  const authedFetch = useCallback(
    async (path: string, init?: RequestInit) => {
      const token = await getToken();
      const res = await fetch(`${API}${path}`, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...(init?.headers ?? {}),
        },
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? `${res.status}`);
      }
      return res.status === 204 ? null : res.json();
    },
    [getToken],
  );

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const [profiles, auths] = await Promise.all([
        authedFetch(`/api/v1/admin/orgs/${orgId}/profiles`) as Promise<{
          jurisdictionProfiles: JurisdictionProfile[];
          assumptionProfiles: AssumptionProfile[];
          governanceEnabled: boolean;
        }>,
        authedFetch(`/api/v1/admin/orgs/${orgId}/authorization-records`) as Promise<{
          records: AuthorizationRecord[];
        }>,
      ]);
      setGovernanceEnabled(profiles.governanceEnabled);
      setJps(profiles.jurisdictionProfiles);
      setAps(profiles.assumptionProfiles);
      setRecords(auths.records);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [orgId, authedFetch]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleGovernance() {
    if (!orgId || governanceEnabled === null) return;
    const next = !governanceEnabled;
    const label = next ? 'enable' : 'disable';
    if (
      !confirm(
        `${label.charAt(0).toUpperCase() + label.slice(1)} governance enforcement for this org? This is audit-logged.`,
      )
    )
      return;
    setTogglingGov(true);
    setError(null);
    try {
      const result = (await authedFetch(`/api/v1/admin/orgs/${orgId}/governance`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: next }),
      })) as { governanceEnabled: boolean };
      setGovernanceEnabled(result.governanceEnabled);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setTogglingGov(false);
    }
  }

  async function submitAuthorization(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId) return;
    setSaving(true);
    setError(null);
    try {
      const scope = formKind === 'profile_change' ? { profileType: formProfileType } : {};
      await authedFetch(`/api/v1/admin/orgs/${orgId}/authorization-records`, {
        method: 'POST',
        body: JSON.stringify({
          kind: formKind,
          signerName: formSigner,
          signerTitle: formTitle,
          documentUrl: formUrl,
          signedAt: new Date(formSignedAt).toISOString(),
          scope,
        }),
      });
      setFormSigner('');
      setFormTitle('');
      setFormUrl('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function revoke(recordId: string) {
    if (!orgId) return;
    if (!confirm('Revoke this authorization record? This action is logged and not reversible.'))
      return;
    try {
      await authedFetch(`/api/v1/admin/orgs/${orgId}/authorization-records/${recordId}/revoke`, {
        method: 'POST',
        body: '{}',
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const hasDefaultJurisdiction = jps.some((p) => p.isDefault);
  const hasDefaultAssumption = aps.some((p) => p.isDefault);
  const hasActiveVendorSetup = records.some(
    (r) => r.kind === 'vendor_setup' && authStatus(r) === 'active',
  );

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <div className="mb-6">
        <Link to={`/admin/orgs/${orgId}`} className="text-xs text-slate-400 hover:underline">
          ← Org overview
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">Governance — {orgId}</h1>
        <p className="mt-1 text-sm text-slate-500">
          Vendor-side setup: profiles + signed authorization records. Every write is audit-logged in
          this org's trail with the operator's Clerk id.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Governance toggle */}
      <div className="mb-6 flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div>
          <div className="text-sm font-semibold text-slate-800">Governance enforcement</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {governanceEnabled
              ? 'Enabled — vendor_setup required, locked-field checks active, profile-change auth gated.'
              : 'Disabled — open access, no authorization gates. Reports include an ungoverned-methodology disclaimer.'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void toggleGovernance()}
          disabled={togglingGov || governanceEnabled === null}
          className={`ml-6 flex-shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
            governanceEnabled
              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {togglingGov ? 'Saving…' : governanceEnabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>

      {/* Bootstrap status */}
      <div className="mb-6 grid grid-cols-3 gap-3 text-xs">
        <StatusCard
          label="Vendor setup"
          ok={governanceEnabled ? hasActiveVendorSetup : true}
          okText={governanceEnabled ? 'Active record on file' : 'Not required (governance off)'}
          missingText="No active vendor_setup — org cannot run first analysis."
        />
        <StatusCard
          label="Jurisdiction profile"
          ok={hasDefaultJurisdiction}
          okText="Default profile set"
          missingText="No default — create one below."
        />
        <StatusCard
          label="Methodology profile"
          ok={hasDefaultAssumption}
          okText="Default profile set"
          missingText="No default — create one below."
        />
      </div>

      {loading && <div className="py-6 text-center text-sm text-slate-500">Loading…</div>}

      {!loading && (
        <>
          {/* Profiles summary */}
          <section className="mb-8">
            <h2 className="mb-3 text-sm font-semibold text-slate-800">Profiles</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <ProfilePanel
                title="Jurisdiction profiles"
                rows={jps.map((p) => ({
                  id: p.id,
                  primary: p.name,
                  secondary: `${p.cityName}${p.countyName ? ` · ${p.countyName}` : ''}`,
                  isDefault: p.isDefault,
                }))}
                emptyHint="No jurisdiction profiles yet."
              />
              <ProfilePanel
                title="Assumption profiles"
                rows={aps.map((p) => ({
                  id: p.id,
                  primary: p.name,
                  secondary: new Date(p.createdAt).toLocaleDateString(),
                  isDefault: p.isDefault,
                }))}
                emptyHint="No assumption profiles yet."
              />
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Full profile editing happens inside the customer org's own Settings UI once an owner
              is active. Impersonate to bootstrap values, or use the dedicated admin edit endpoints
              (see{' '}
              <code className="font-mono">/api/v1/admin/orgs/:orgId/jurisdiction-profiles</code>
              ).
            </p>
          </section>

          {/* Authorization records */}
          <section className="mb-8">
            <h2 className="mb-3 text-sm font-semibold text-slate-800">Authorization records</h2>
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Kind</th>
                    <th className="px-3 py-2 text-left">Signer</th>
                    <th className="px-3 py-2 text-left">Signed</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {records.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-slate-400">
                        No records.
                      </td>
                    </tr>
                  )}
                  {records.map((r) => {
                    const status = authStatus(r);
                    return (
                      <tr key={r.id}>
                        <td className="px-3 py-2 font-mono text-xs text-slate-700">{r.kind}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-900">{r.signerName}</div>
                          <div className="text-xs text-slate-500">{r.signerTitle}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {new Date(r.signedAt).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2">
                          <StatusBadge status={status} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <a
                            href={r.documentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mr-3 text-xs text-indigo-600 hover:underline"
                          >
                            Doc
                          </a>
                          {!r.revokedAt && (
                            <button
                              type="button"
                              onClick={() => void revoke(r.id)}
                              className="text-xs text-red-600 hover:underline"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* New authorization form */}
          <section>
            <h2 className="mb-3 text-sm font-semibold text-slate-800">
              Record a new authorization
            </h2>
            <form
              onSubmit={(e) => void submitAuthorization(e)}
              className="rounded-lg border border-slate-200 bg-white p-4 text-sm shadow-sm"
            >
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Kind" htmlFor="auth-kind">
                  <select
                    id="auth-kind"
                    value={formKind}
                    onChange={(e) => setFormKind(e.target.value as AuthorizationRecord['kind'])}
                    className="w-full rounded border border-slate-300 px-2 py-1.5"
                  >
                    <option value="vendor_setup">vendor_setup</option>
                    <option value="profile_change">profile_change</option>
                    <option value="consultant_override">consultant_override</option>
                  </select>
                </Field>
                {formKind === 'profile_change' && (
                  <Field label="Profile type (scope)" htmlFor="auth-profile-type">
                    <select
                      id="auth-profile-type"
                      value={formProfileType}
                      onChange={(e) =>
                        setFormProfileType(e.target.value as 'jurisdiction' | 'assumption')
                      }
                      className="w-full rounded border border-slate-300 px-2 py-1.5"
                    >
                      <option value="jurisdiction">jurisdiction</option>
                      <option value="assumption">assumption</option>
                    </select>
                  </Field>
                )}
                <Field label="Signer name" htmlFor="auth-signer-name">
                  <input
                    id="auth-signer-name"
                    required
                    value={formSigner}
                    onChange={(e) => setFormSigner(e.target.value)}
                    className="w-full rounded border border-slate-300 px-2 py-1.5"
                  />
                </Field>
                <Field label="Signer title" htmlFor="auth-signer-title">
                  <input
                    id="auth-signer-title"
                    required
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    className="w-full rounded border border-slate-300 px-2 py-1.5"
                  />
                </Field>
                <Field label="Signed document URL" htmlFor="auth-doc-url">
                  <input
                    id="auth-doc-url"
                    required
                    type="url"
                    value={formUrl}
                    onChange={(e) => setFormUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full rounded border border-slate-300 px-2 py-1.5"
                  />
                </Field>
                <Field label="Signed at" htmlFor="auth-signed-at">
                  <input
                    id="auth-signed-at"
                    required
                    type="date"
                    value={formSignedAt}
                    onChange={(e) => setFormSignedAt(e.target.value)}
                    className="w-full rounded border border-slate-300 px-2 py-1.5"
                  />
                </Field>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                >
                  {saving ? 'Recording…' : 'Record authorization'}
                </button>
              </div>
            </form>
          </section>
        </>
      )}
    </div>
  );
}

function StatusCard({
  label,
  ok,
  okText,
  missingText,
}: {
  label: string;
  ok: boolean;
  okText: string;
  missingText: string;
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        ok ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
      }`}
    >
      <div className="font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 ${ok ? 'text-emerald-800' : 'text-amber-800'}`}>
        {ok ? okText : missingText}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'active' | 'expired' | 'revoked' }) {
  const cls =
    status === 'active'
      ? 'bg-emerald-50 text-emerald-700'
      : status === 'expired'
        ? 'bg-amber-50 text-amber-700'
        : 'bg-slate-100 text-slate-500';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function ProfilePanel({
  title,
  rows,
  emptyHint,
}: {
  title: string;
  rows: { id: string; primary: string; secondary: string; isDefault: boolean }[];
  emptyHint: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </div>
      {rows.length === 0 && <div className="text-xs text-slate-400">{emptyHint}</div>}
      {rows.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-2">
              <div>
                <div className="text-sm font-medium text-slate-900">{r.primary}</div>
                <div className="text-xs text-slate-500">{r.secondary}</div>
              </div>
              {r.isDefault && (
                <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                  default
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="block">
      <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-slate-600">
        {label}
      </label>
      {children}
    </div>
  );
}
